import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  alpha,
  useTheme
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useThemeSettings } from '../../../../contexts/ThemeContext';
import { formatDate } from '../../utils/ticketTransformers';
import { getTokenFromStorage } from '../../utils/ticketTransformers';

/**
 * Messages tab for ticket communication
 */
const MessagesTab = ({
  messages,
  ticketId,
  interventionDate,
  onSendMessage
}) => {
  const { t } = useThemeSettings();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [newMessage, setNewMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
    event.target.value = ''; // Reset input
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!newMessage.trim() && selectedFiles.length === 0) return;

    setSending(true);
    try {
      await onSendMessage(newMessage, selectedFiles);
      setNewMessage('');
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error sending message:', error);
      enqueueSnackbar(t ? t('Tickets.send_failed') : 'Failed to send message', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
      const downloadUrl = attachment.file_id
        ? `${API_BASE_URL}/api/files/${attachment.file_id}/download?inline=false`
        : `https://files.caro.care${attachment.url}`;

      const token = await getTokenFromStorage('auth_token');
      const response = await fetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.originalName || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      enqueueSnackbar('Failed to download file', { variant: 'error' });
    }
  };

  return (
    <Box>
      {/* Messages List */}
      <Box sx={{ mb: 3 }}>
        {messages.length > 0 ? (
          messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 1,
                bgcolor: message.is_system_message
                  ? alpha(theme.palette.info.main, 0.1)
                  : alpha(theme.palette.grey[100], 0.5),
                borderLeft: message.is_system_message
                  ? `4px solid ${theme.palette.info.main}`
                  : 'none'
              }}
            >
              {/* Message Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" component="span">
                  {message.is_system_message ? 'System' : (
                    message.User?.username || message.User?.name ||
                    message.user?.username || message.user?.name ||
                    `${message.User?.firstName || message.user?.firstName || ''} ${message.User?.lastName || message.user?.lastName || ''}`.trim() ||
                    'Unknown'
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(message.created_at)}
                </Typography>
              </Box>

              {/* Message Content */}
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {message.message}
              </Typography>

              {/* Intervention Notice */}
              {message.is_system_message && message.message?.includes('Physical intervention') && interventionDate && (
                <Box sx={{ mt: 1, p: 1, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 1 }}>
                  <Typography variant="body2" color="warning.main" fontWeight="medium">
                    {t ? t("Tickets.scheduled") : "Scheduled for: "}
                    {new Date(interventionDate).toLocaleDateString('en-GB', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                </Box>
              )}

              {/* Message Attachments */}
              {message.TicketAttachments && message.TicketAttachments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {message.TicketAttachments.map((attachment) => (
                    <Chip
                      key={attachment.id}
                      icon={<FileIcon />}
                      label={attachment.originalName}
                      onClick={() => handleDownloadAttachment(attachment)}
                      sx={{ mr: 1, mb: 1, cursor: 'pointer' }}
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              )}
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            {t ? t("Tickets.no_messages_yet") : "No messages yet."}
          </Typography>
        )}
      </Box>

      {/* Selected Files Preview */}
      {selectedFiles.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {selectedFiles.map((file, index) => (
            <Chip
              key={index}
              icon={<FileIcon />}
              label={file.name}
              onDelete={() => handleRemoveFile(index)}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      )}

      {/* Message Input */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <input
          type="file"
          multiple
          style={{ display: 'none' }}
          id="message-file-upload"
          onChange={handleFileSelect}
        />
        <label htmlFor="message-file-upload">
          <IconButton component="span" color="primary" disabled={sending}>
            <AttachFileIcon />
          </IconButton>
        </label>

        <TextField
          fullWidth
          variant="outlined"
          placeholder={t ? t("Tickets.type_your_message") : "Type your message..."}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          multiline
          maxRows={4}
          disabled={sending}
          size="small"
        />

        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleSend}
          disabled={(!newMessage.trim() && selectedFiles.length === 0) || sending}
          sx={{ minWidth: '100px' }}
        >
          {sending ? 'Sending...' : 'Send'}
        </Button>
      </Box>
    </Box>
  );
};

export default MessagesTab;
